"use client";
import { useEffect, useState } from "react";
import { check_auth, postBlogComment, getBlogComments } from "../../../api";
import parse from "html-react-parser";

const BlogComments = ({ blogId }) => {
  const [comments, setComments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showNameModal, setShowNameModal] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [nameError, setNameError] = useState("");
  const [pendingComment, setPendingComment] = useState("");

  console.log("comments", comments);

  // 🔐 Auth check
  useEffect(() => {
    const getAuthStatus = async () => {
      try {
        const session = await check_auth();
        setIsLoggedIn(!!session);
      } catch {
        setIsLoggedIn(false);
      }
    };
    getAuthStatus();
  }, []);

  // 📥 Fetch comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const data = await getBlogComments(blogId);
        setComments(data.comments || []);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      }
    };

    if (blogId) fetchComments();
  }, [blogId]);

  // 📝 Handle comment submission
  const postComment = async (content, name = "") => {
    setIsSubmitting(true);
    try {
      const newComment = await postBlogComment(
        blogId,
        content,
        name,
        isLoggedIn,
      );
      setComments((prev) => [newComment, ...prev]);
      setCommentText("");
    } catch (error) {
      console.error("Failed to post comment:", error);
      alert("Error posting comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentSubmit = async () => {
    const content = commentText.trim();
    if (!content) return;

    if (!isLoggedIn) {
      setPendingComment(content);
      setGuestName("");
      setNameError("");
      setShowNameModal(true);
      return;
    }

    await postComment(content);
  };

  const handleGuestSubmit = async () => {
    const cleanedName = guestName.trim();
    if (!cleanedName) {
      setNameError("Please enter your name.");
      return;
    }

    setNameError("");
    setShowNameModal(false);
    await postComment(pendingComment, cleanedName);
    setPendingComment("");
  };

  const formatCommentDate = (timestamp) => {
    if (!timestamp) return "Date unavailable";

    let date;

    // Already a Date
    if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Number (seconds or ms)
    else if (typeof timestamp === "number") {
      date =
        timestamp.toString().length === 10
          ? new Date(timestamp * 1000)
          : new Date(timestamp);
    }
    // RFC 1123 / HTTP-date string
    else if (typeof timestamp === "string") {
      // Example: "Wed, 18 Feb 2026 05:56:39 GMT"
      const parsed = Date.parse(timestamp);
      if (!Number.isNaN(parsed)) {
        date = new Date(parsed);
      } else {
        return "Date unavailable";
      }
    } else {
      return "Date unavailable";
    }

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="mt-16">
      <h2 className="text-2xl font-bold mb-4">Comments</h2>

      <div className="mb-8">
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            rows={4}
            placeholder="Write your comment..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-[#0D6FEA] resize-y"
          />
          <button
            type="button"
            onClick={handleCommentSubmit}
            disabled={!commentText.trim() || isSubmitting}
            className="mt-3 px-4 py-2 bg-primary text-white rounded-md disabled:opacity-60 cursor-pointer"
          >
            {isSubmitting ? "Posting..." : "Post Comment"}
          </button>
        </div>
        {isSubmitting && (
          <p className="text-sm text-gray-500 mt-2">Submitting comment...</p>
        )}
      </div>

      <div className="space-y-6">
        {comments.length > 0 ? (
          comments.map((comment) => (
            <div key={comment.id} className="p-4 border rounded-lg bg-gray-50">
              <div className="flex items-center mb-2">
                <p className="font-semibold mr-4">
                  {comment.user_name || "Anonymous"}
                </p>
                <p className="text-xs text-gray-500">
                  {formatCommentDate(comment.created_at)}
                </p>
              </div>
              <div className="prose prose-sm max-w-none">
                {parse(comment.text || "")}
              </div>
            </div>
          ))
        ) : (
          <p>No comments yet. Be the first to comment!</p>
        )}
      </div>

      {showNameModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl border border-gray-100">
            <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">
              Enter your name
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Add your name to post this comment.
            </p>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-[#0D6FEA]"
            />
            {nameError ? (
              <p className="text-sm text-red-600 mt-2">{nameError}</p>
            ) : null}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowNameModal(false);
                  setPendingComment("");
                  setNameError("");
                }}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleGuestSubmit}
                className="px-4 py-2 rounded-md bg-primary text-white cursor-pointer"
              >
                Post Comment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogComments;
